import {
  Button,
  Card,
  FormControl,
  FormField,
  Parameter,
  Parameters,
  Separator,
} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import {GetMinimumMarginCustomRecalculationApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, getPercentageFromDecimal} from '@dms/shared';

import {buildArray, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {PRICING_FIELD_WIDTH} from '../constants/PRICING_FIELD_WIDTH';
import {useApplyCurrentMinimumMarginSettings} from '../hooks/useApplyCurrentMinimumMarginSettings';
import {DiscountSectionFormType} from '../schemas/discountSectionSchema';

interface MinimumMarginCardProps extends RequiredTestIdProps {
  control: FormControl<DiscountSectionFormType>;
  formApi: UseFormReturn<DiscountSectionFormType>;
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
  minimumMarginData: GetMinimumMarginCustomRecalculationApiResponse | Nullish;
}

const formatMargin = (label: string, margin: number | Nullish) => {
  const percentage = getPercentageFromDecimal(margin);

  return isNil(percentage)
    ? `${label}: ${EMPTY_PLACEHOLDER}`
    : `${label}: ${percentage}${i18n.t('general.metric.percentage')}`;
};

export function MinimumMarginCard(props: MinimumMarginCardProps) {
  const isExpanded = useWatch({control: props.control, name: 'isMinimumMarginExpanded'});

  const {applyCurrentSettings, isApplyingCurrentSettings} = useApplyCurrentMinimumMarginSettings({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
    formApi: props.formApi,
  });

  const hasMargins =
    !isNil(props.minimumMarginData?.materialMargin) ||
    !isNil(props.minimumMarginData?.labourMargin);
  const hasMismatch = props.minimumMarginData?.matchesCurrentSettings === false;

  const marginParameters = buildArray<Parameter>([
    isExpanded && !hasMismatch
      ? i18n.t('entity.order.labels.defaultMinimumMargin')
      : i18n.t('entity.order.labels.currentMinimumMargin'),
    formatMargin(i18n.t('entity.order.labels.material'), props.minimumMarginData?.materialMargin),
    formatMargin(i18n.t('entity.order.labels.work'), props.minimumMarginData?.labourMargin),
  ])
    .when(hasMismatch, {
      title: i18n.t('entity.order.labels.marginDoesNotMatchCurrentSettings'),
      icon: 'alert/error',
      variant: 'danger',
    })
    .when(
      hasMismatch,
      formatMargin(
        i18n.t('entity.order.labels.material'),
        props.minimumMarginData?.serviceOrderVariantMaterialMargin
      )
    )
    .when(
      hasMismatch,
      formatMargin(
        i18n.t('entity.order.labels.work'),
        props.minimumMarginData?.serviceOrderVariantLabourMargin
      )
    );

  return (
    <Card variant="inlineGrey">
      <VStack spacing={4}>
        <VStack spacing={1}>
          <Heading size={4}>{i18n.t('entity.order.labels.minimumMargin')}</Heading>
          <Show when={hasMargins || hasMismatch}>
            <Parameters
              parameters={marginParameters}
              size="small"
              align="center"
              appendix={
                <Show when={hasMismatch && !props.isReadOnly}>
                  <Button
                    variant="link"
                    size="small"
                    leftIcon="navigation/refresh"
                    title={i18n.t('entity.order.actions.getCurrentSettings')}
                    isLoading={isApplyingCurrentSettings}
                    isDisabled={isApplyingCurrentSettings}
                    onClick={applyCurrentSettings}
                    data-testid={suffixTestId('getCurrentSettings', props)}
                  />
                </Show>
              }
              data-testid={suffixTestId('currentMinimumMargin', props)}
            />
          </Show>
        </VStack>
        <Separator spacing={0} />
        <HStack align="center" spacing={3}>
          <FormField
            control={props.control}
            name="isMinimumMarginExpanded"
            type="switch"
            isDisabled={props.isReadOnly}
            data-testid={suffixTestId('expand-switch', props)}
          />
          <Heading size={4}>{i18n.t('entity.order.labels.customMinimumMargin')}</Heading>
        </HStack>
        <Show when={isExpanded}>
          <HStack spacing={4}>
            <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
              <FormField
                control={props.control}
                name="minimumMargin.material"
                type="number"
                label={i18n.t('entity.serviceOrder.labels.minimumMaterialMargin')}
                decimalPlaces={2}
                suffix={i18n.t('general.metric.percentage')}
                isRequired
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('materialMargin', props)}
              />
            </Box>
            <Box width={PRICING_FIELD_WIDTH} minWidth={0}>
              <FormField
                control={props.control}
                name="minimumMargin.labour"
                type="number"
                label={i18n.t('entity.serviceOrder.labels.minimumLabourMargin')}
                decimalPlaces={2}
                suffix={i18n.t('general.metric.percentage')}
                isRequired
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('labourMargin', props)}
              />
            </Box>
          </HStack>
        </Show>
      </VStack>
    </Card>
  );
}
